import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import TreatmentPeriodCard from "./TreatmentPeriodCard";
import CurrentTherapyStatusCard from "./CurrentTherapyStatusCard";
import SpecialFeatureCard from "./SpecialFeatureCard";
import ChangeOfTreatmentCard from "./ChangeOfTreatmentCard";
import TreatmentDiscontinuationCard from "./TreatmentDiscontinuationCard";
import ContinueTreatmentCard from "./ContinueTreatmentCard";
import PrognosticAssessmentCard from "./PrognosticAssessmentCard";
import TherapyChangeSuggestionsCard from "./TherapyChangeSuggestionsCard";
import PrintingAndShippingProtocolCard from "./PrintingAndShippingProtocolCard";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  DoctorReportDto,
  DoctorReportStatusDto,
  DoctorReportStatusDtoStatusEnum,
  DoctorReportTimeSlotDto,
  UserDto,
} from "../../../../api-client";
import useStore from "../../../../helpers/useStore";
import { AppointmentContext } from "../../../../stores/Appointment/appointment.provider";
import { useApi } from "../../../../api/ApiProvider";
import Form from "../../../PatientDataEntryForm/Form";
import { PatientsContext } from "../../../../stores/Patients/patients.provider";
import notificationStore from "../../../../stores/Notification/notificationStore";
import { useLocationId } from "../../../../stores/User/User.hooks";
import { useUsersByLocation } from "../../../../stores/Calendar/calendar.queries";

interface DoctorReportFormProps {
  editMode: boolean;
  onUpdateReportStatus?: (doctorReportStatus: DoctorReportStatusDto) => void;
  doctorReport:DoctorReportDto;
  setDoctorReport:(doctorReport:DoctorReportDto)=>void;
  rxId: string | null;
  handleRowClick?: (report: DoctorReportDto) => void;
  handleCloseDialog:(value?:any) => void;
  onSave?:()=>void;
  routingParams?: any;
} 

export interface DoctorReportFormRef {
  handleSubmitDoctorReport: () => void;
}

const DoctorReportForm = forwardRef<DoctorReportFormRef, DoctorReportFormProps>(
  ({ editMode, onUpdateReportStatus, rxId,doctorReport,setDoctorReport,handleRowClick,handleCloseDialog,onSave,routingParams }, ref) => {
    const { clinicalDocumentation } = useApi();
    const PatientStore=useStore(PatientsContext);
    // const [doctorReport, setDoctorReport] = useState<DoctorReportDto>({
    //   id:  "",
    //   rxId: rxId ? rxId: "",
    // });

    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const fetchData = async () => {
      if(rxId){
        setLoading(true);
        try {
          const report = await clinicalDocumentation.getClinicalDoctorReportByRxId(rxId);
          setDoctorReport(report);
        } catch (error) {
          // console.log(`Error fetching doctor report for rxId ${rxId}`, error);
        } finally {
          setLoading(false);
        }
      }
    };

    useEffect(() => {
      if (rxId) {
        fetchData()
       
      }
    }, [rxId]);
    useImperativeHandle(ref, () => ({
      async handleSubmitDoctorReport() {
        setLoading(true);
        let doctorReportObj = { ...doctorReport };
    
        try {
          if (doctorReportObj.id) {
            // Update doctor report
            const updatedReport = await clinicalDocumentation.updateClinicalDoctorReport(doctorReportObj.id, doctorReportObj) as unknown as DoctorReportDto;
            setDoctorReport(updatedReport);

            // Save assignment change if needed
            if (doctorReportObj.assignedToUserId) {
              await clinicalDocumentation.assignClinicalDoctorReport(doctorReportObj.id, { assignedToUserId: doctorReportObj.assignedToUserId });
            }

            // Determine status to apply: an explicit pending status takes
            // precedence, otherwise auto-transition Abgegeben -> Versendet once
            // the report has been dispatched (printed and/or faxed).
            const currentStatus = (doctorReportObj as any).status;
            const hasDispatch = !!(doctorReportObj.printedAt || doctorReportObj.sentByFaxAt);
            const statusToApply =
              pendingStatus ||
              (hasDispatch && currentStatus === DoctorReportStatusDtoStatusEnum.Abgegeben
                ? DoctorReportStatusDtoStatusEnum.Versendet
                : null);

            // Save status change if needed
            if (statusToApply) {
              const statusData = await clinicalDocumentation.updateClinicalDoctorReportStatus(doctorReportObj.id, { status: statusToApply } as any);
              onUpdateReportStatus && onUpdateReportStatus(statusData);
              setPendingStatus(null);
            }

            notificationStore.showMessage("Therapiebericht erfolgreich gespeichert", "success");
          } else {
            // Create new doctor report
            let createDoctorReportObj = { ...doctorReportObj, id: undefined };
            const newReport = await clinicalDocumentation.postClinicalDoctorReport(createDoctorReportObj) as unknown as DoctorReportDto;
            setDoctorReport(newReport);
            notificationStore.showMessage("Therapiebericht erfolgreich gespeichert", "success");
            if(handleRowClick){
            handleRowClick(newReport)}
          }
        } catch (error: any) {
          console.error("Error in submitting doctor report:", error);
          notificationStore.showMessage(error?.message, 'error', error);
          throw error; // Re-throw to let the caller handle it
        } finally {
          setLoading(false);
        }
      },
    }));
    

    // useImperativeHandle(ref, () => ({
    //   handleSubmitDoctorReport() {
    //     setLoading(true);
    //     let doctorReportObj = { ...doctorReport };

    //     if (doctorReportObj)
    //       if (doctorReportObj.id) {
    //         // Update doctor report
    //         updateDoctorReport(doctorReportObj.id, doctorReportObj)
    //           .then((data) => {
    //             setDoctorReport(data);
    //           })
    //           .catch((error) => {
    //             console.log(error);
    //           })
    //           .finally(() => setLoading(false));
    //       } else {
    //         let createDoctorReportObj = { ...doctorReportObj, id: undefined };
    //         createDoctorReport(createDoctorReportObj)
    //           .then((doctorReport) => {
    //             setDoctorReport(doctorReport);

    //           })
    //           .catch((error) => {
    //             console.log(error);
    //           })
    //           .finally(() => setLoading(false));
    //       }
    //       // fetchData()
    //   },
    // }));



    const handleCompleteDoctorReport = async () => {
      setLoading(true);
      try {
        const data = await clinicalDocumentation.updateClinicalDoctorReportStatus(doctorReport.id, { status: DoctorReportStatusDtoStatusEnum.Abgegeben });

        if ((data as any).existingTicketId) {
          const rawStatus = (data as any).existingTicketStatus || "";
          const statusMap: Record<string, string> = {
            "Geschlossen": "CLOSED", "Neu": "NEW", "In Bearbeitung": "IN_PROGRESS",
            "Ausstehend": "PENDING", "Bestellt": "ORDERED", "Genehmigt": "APPROVED", "Abgelehnt": "REJECTED",
          };
          const ticketStatus = statusMap[rawStatus] || rawStatus.toUpperCase() || "UNKNOWN";
          notificationStore.showMessage(
            `Status aktualisiert. Ein Ticket existiert bereits (Ticket-ID: ${(data as any).existingTicketId}, Status: ${ticketStatus}).`,
            "warning"
          );
        } else if ((data as any).ticketCreated) {
          notificationStore.showMessage("Status aktualisiert und neues Ticket erstellt!", "success");
        } else {
          notificationStore.showMessage("Status des Therapieberichts erfolgreich aktualisiert!", "success");
        }

        onUpdateReportStatus && onUpdateReportStatus(data);
        if(handleCloseDialog){
          handleCloseDialog(true);
        }
      } catch (error: any) {
        // console.log(error);
        notificationStore.showMessage("Der Status des Therapieberichts konnte nicht aktualisiert werden!", "error");
      } finally {
        setLoading(false);
      }
    };

    // --- Therapist Assignment & Status Change ---
    const locationId = useLocationId();
    const { data: locationUsers = [] } = useUsersByLocation(locationId, undefined, true, !!locationId && editMode);
    const [pendingStatus, setPendingStatus] = useState<DoctorReportStatusDtoStatusEnum | null>(null);

    const handleAssignmentChange = (userId: string) => {
      setDoctorReport({ ...doctorReport, assignedToUserId: userId });
    };

    const handleStatusChange = (newStatus: DoctorReportStatusDtoStatusEnum) => {
      setPendingStatus(newStatus);
    };

    const FormContent=(
      <Form>
      {loading && 
        <div>
          <CircularProgress size={60} sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }} />
          Loading...
        </div>
    }
    {!loading && (
      <Grid container spacing={6}>
        {/* Assignment & Status Controls */}
        {editMode && doctorReport.id && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 250 }}
                options={locationUsers}
                getOptionLabel={(user: UserDto) =>
                  `${user.firstName || ""} ${user.lastName || ""}`.trim()
                }
                value={
                  locationUsers.find((u) => u.id === doctorReport.assignedToUserId) || null
                }
                onChange={(_e, newValue) => {
                  if (newValue?.id) handleAssignmentChange(newValue.id);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Zugewiesen an" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
              <Select
                size="small"
                sx={{ minWidth: 180 }}
                value={pendingStatus || (doctorReport as any).status || ""}
                onChange={(e) => handleStatusChange(e.target.value as DoctorReportStatusDtoStatusEnum)}
                displayEmpty
              >
                <MenuItem value="" disabled>Status</MenuItem>
                <MenuItem value={DoctorReportStatusDtoStatusEnum.Entwurf}>Entwurf</MenuItem>
                <MenuItem value={DoctorReportStatusDtoStatusEnum.Abgegeben}>Abgegeben</MenuItem>
                <MenuItem value={DoctorReportStatusDtoStatusEnum.Versendet}>Versendet</MenuItem>
              </Select>
            </Box>
          </Grid>
        )}
        <Grid item xs={6}>
          <Grid sx={{ marginBottom: 2 }}>
        <TreatmentPeriodCard
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
          editMode={editMode}
        /></Grid>
         <Grid sx={{ marginBottom: 2 }}>
        <CurrentTherapyStatusCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <SpecialFeatureCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <ChangeOfTreatmentCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        </Grid>
        <Grid item xs={6}>
        <Grid sx={{ marginBottom: 2 }}>
        <TreatmentDiscontinuationCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <ContinueTreatmentCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <PrognosticAssessmentCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <TherapyChangeSuggestionsCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        <Grid sx={{ marginBottom: 2 }}>
        <PrintingAndShippingProtocolCard
          editMode={editMode}
          doctorReport={doctorReport}
          setDoctorReport={setDoctorReport}
        /></Grid>
        </Grid>
      </Grid>
    )}
    {editMode && doctorReport.id && (() => {
      const effectiveStatus = pendingStatus || (doctorReport as any).status;
      return !effectiveStatus || effectiveStatus === DoctorReportStatusDtoStatusEnum.Entwurf;
    })() && (
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <Button variant="contained" color="primary" onClick={() =>setOpenDialog(true)
        }>
          Abgeben
        </Button>
      </div>
    )}

    <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
      <DialogTitle>Sind Sie sicher, dass Sie das Formular ausfüllen möchten?</DialogTitle>
      <DialogContent>
        Sie werden das Formular später nicht mehr aktualisieren können.
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)} color="secondary">
          Nein
        </Button>
        <Button
          onClick={() => {
            handleCompleteDoctorReport();
            setOpenDialog(false);
            
          }}
          color="primary"
        >
          Ja
        </Button>
      </DialogActions>
    </Dialog></Form>)

return (
  editMode ? (
    <Dialog open={editMode} onClose={handleCloseDialog} fullWidth maxWidth="lg">
      <DialogTitle>
        {editMode ? "Therapiebericht bearbeiten" : "Therapiebericht"}
      </DialogTitle>
      <DialogContent>  
        {FormContent}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="text">
          Abbrechen
        </Button>
        <Button onClick={onSave} variant="contained" disabled={!editMode}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    FormContent // Render FormContent if not in editReportMode
  )
);


  }
);

export default DoctorReportForm;
